/**
 * Simple Session-based Authentication Context
 * Replaces complex JWT localStorage system with session cookies
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { simpleAuthAPI } from '../services/simpleAuthAPI';

interface User {
  id: number;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  emailVerified: boolean;
  isAlphaUser: boolean;
  registrationSource: string;
  createdAt: string;
  lastLogin?: string;
  lastActivity?: string;
}

interface SimpleAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Core auth methods
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string, invitationCode?: string) => Promise<void>;
  
  // Additional methods
  verifyEmail: (token: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  
  // Utility methods
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

const SimpleAuthContext = createContext<SimpleAuthContextType | undefined>(undefined);

export const useSimpleAuth = () => {
  const context = useContext(SimpleAuthContext);
  if (!context) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider');
  }
  return context;
};

interface SimpleAuthProviderProps {
  children: React.ReactNode;
}

export const SimpleAuthProvider: React.FC<SimpleAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Derived state
  const isAuthenticated = user !== null;

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleAuthError = useCallback((error: any) => {
    console.error('[SimpleAuth] Authentication error:', error);
    const message = error?.message || error?.error || 'Authentication failed';
    setError(message);
    
    // If it's a 401 error, clear user state
    if (error?.status === 401 || message.includes('401') || message.includes('Authentication required')) {
      setUser(null);
    }
  }, []);

  // Check authentication status using session cookies
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('[SimpleAuth] Checking authentication status...');
      
      const result = await simpleAuthAPI.checkAuthentication();
      
      if (result.success && result.authenticated && result.user) {
        setUser(result.user);
        setError(null);
        console.log('[SimpleAuth] User authenticated:', result.user.email);
      } else {
        setUser(null);
        console.log('[SimpleAuth] User not authenticated');
      }
      
    } catch (error: any) {
      console.error('[SimpleAuth] Auth check failed:', error);
      setUser(null);
      // Don't set error for auth check - it's expected to fail for unauthenticated users
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize authentication on mount
  useEffect(() => {
    console.log('[SimpleAuth] Initializing authentication...');
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[SimpleAuth] Attempting login...');
      const response = await simpleAuthAPI.login(email, password, rememberMe);
      
      if (response.success && response.user) {
        setUser(response.user);
        console.log('[SimpleAuth] Login successful:', response.user.email);
        
        // Session cookie is automatically set by the server
        console.log('[SimpleAuth] Session cookie should be set automatically');
      } else {
        throw new Error('Login failed: Invalid response');
      }
    } catch (error: any) {
      handleAuthError(error);
      throw error; // Re-throw for component handling
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('[SimpleAuth] Attempting logout...');
      
      await simpleAuthAPI.logout();
      
      // Clear user state
      setUser(null);
      setError(null);
      
      console.log('[SimpleAuth] Logout successful - session cookie cleared');
      
    } catch (error: any) {
      console.error('[SimpleAuth] Logout error (non-critical):', error);
      // Still clear user state even if logout request failed
      setUser(null);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string,
    invitationCode?: string
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[SimpleAuth] Attempting registration...');
      const response = await simpleAuthAPI.register(email, password, firstName, lastName, invitationCode);
      
      if (response.success) {
        console.log('[SimpleAuth] Registration successful');
        // Note: User will need to verify email before logging in
      } else {
        throw new Error('Registration failed: Invalid response');
      }
      
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  const verifyEmail = useCallback(async (token: string): Promise<boolean> => {
    try {
      setError(null);
      console.log('[SimpleAuth] Verifying email...');
      
      const success = await simpleAuthAPI.verifyEmail(token);
      
      if (success) {
        console.log('[SimpleAuth] Email verified successfully');
        // Refresh user data to get updated emailVerified status
        await checkAuth();
      } else {
        console.log('[SimpleAuth] Email verification failed');
      }
      
      return success;
    } catch (error: any) {
      handleAuthError(error);
      return false;
    }
  }, [handleAuthError, checkAuth]);

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      setError(null);
      console.log('[SimpleAuth] Requesting password reset...');
      
      await simpleAuthAPI.requestPasswordReset(email);
      
      console.log('[SimpleAuth] Password reset requested');
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    }
  }, [handleAuthError]);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    try {
      setError(null);
      console.log('[SimpleAuth] Resetting password...');
      
      await simpleAuthAPI.resetPassword(token, newPassword);
      
      console.log('[SimpleAuth] Password reset successful');
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    }
  }, [handleAuthError]);

  const contextValue: SimpleAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    verifyEmail,
    requestPasswordReset,
    resetPassword,
    checkAuth,
    clearError,
  };

  return (
    <SimpleAuthContext.Provider value={contextValue}>
      {children}
    </SimpleAuthContext.Provider>
  );
};

export default SimpleAuthProvider;